import React, { useEffect, useReducer, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

import PanelHeader from '../common/cards/PanelHeader';
import communicationIcon from '../../images/icons/communication.svg';
import CommunicationsDispatch from '../../contexts/communications.dispatch';
import communicationsReducer from '../../reducers/communications.reducer';
import CommunicationPanelCard from '../common/cards/CommunicationPanelCard';
import format from '../../helpers/format';

import style from './CommunicationsPanel.module.css';

const CommunicationsPanel = React.memo(({ clientId }) => {
  const [cards, setCards] = useState([]);
  const [newest, setNewest] = useState(true);
  const [communicationsState, communicationsDispatch] = useReducer(
    communicationsReducer,
    {
      date: {
        thirtyDays: false,
        sixtyDays: false,
        ninetyDays: false,
        oneYear: true,
        all: false,
      },
      category: {
        documents: true,
        endorsements: true,
        bindRequests: true,
        otherService: true,
        policyCancel: true,
        policyEvents: true,
      },
    },
  );

  useEffect(() => {
    const convertToDays = () => {
      if (communicationsState.date.thirtyDays) {
        return 30;
      }

      if (communicationsState.date.sixtyDays) {
        return 60;
      }

      if (communicationsState.date.ninetyDays) {
        return 90;
      }

      if (communicationsState.date.oneYear) {
        return 365;
      }

      return '';
    };

    const getCategories = () => {
      let categories = '';
      if (communicationsState.category.documents) {
        categories += `&activityType=document`;
      }

      if (communicationsState.category.endorsements) {
        categories += `&activityType=endorsements`;
      }

      if (communicationsState.category.bindRequests) {
        categories += `&activityType=bindrequest`;
      }

      if (communicationsState.category.otherService) {
        categories += `&activityType=otherservicerequests`;
      }

      if (communicationsState.category.policyCancel) {
        categories += `&activityType=policycancellationrequests`;
      }

      if (communicationsState.category.policyEvents) {
        categories += `&activityType=policyevents`;
      }

      return categories;
    };

    const handleSuccess = ({ data }) => {
      setCards(data.Cards);
    };

    axios
      .get(
        `${
          process.env.REACT_APP_BASE_URL
        }panels/communications/${clientId}?days=${convertToDays()}${getCategories()}`,
      )
      .then(handleSuccess);
  }, [clientId, communicationsState]);

  const handleSorting = (event, isNewest) => {
    event.preventDefault();

    setNewest(isNewest);

    cards.sort((a, b) => {
      return newest
        ? new Date(a.Date) - new Date(b.Date)
        : new Date(b.Date) - new Date(a.Date);
    });

    setCards(cards);
  };

  return (
    <CommunicationsDispatch.Provider value={communicationsDispatch}>
      <section className={style.main}>
        <PanelHeader
          communications
          icon={communicationIcon}
          title="COMMUNICATIONS"
        />
        <div className={style.sort}>
          <a
            className={newest ? style.active : ''}
            onClick={event => handleSorting(event, true)}
            href="#newest"
          >
            Newest
          </a>
          <span> | </span>
          <a
            className={!newest ? style.active : ''}
            onClick={event => handleSorting(event, false)}
            href="#oldest"
          >
            Oldest
          </a>
        </div>
        {cards.map(item => {
          switch (item.Type) {
            case 'Document':
              return (
                <CommunicationPanelCard
                  key={item.Data.Id}
                  clientId={clientId}
                  fileId={item.Data.Id}
                  title={item.Title}
                  description={item.Data.Description}
                  data={[
                    {
                      label: 'Date Created',
                      value: format.formatDate(item.Date),
                      type: 'default',
                    },
                  ]}
                />
              );
            case 'PolicyEvents':
              return (
                <CommunicationPanelCard
                  key={`${item.Data.PolicyId}${item.Date}`}
                  clientId={clientId}
                  title={item.Title}
                  data={[
                    {
                      label: 'Policy ID #',
                      value: item.Data.PolicyId,
                      type: 'default',
                    },
                    {
                      label: 'Policy Number',
                      value: item.Data.PolicyNumber,
                      type: 'default',
                    },
                    {
                      label: 'Carrier',
                      value: item.Data.BillingCarrier,
                      type: 'default',
                    },
                    {
                      label: 'Premium',
                      value: item.Data.Premium,
                      type: 'default',
                    },
                    {
                      label: 'Policy Term',
                      value: `${format.formatDate(
                        item.Data.EffectiveDate,
                      )} to ${format.formatDate(item.Data.ExpirationDate)}`,
                      type: 'default',
                    },
                  ]}
                />
              );
            case 'OtherServiceRequests':
              return (
                <CommunicationPanelCard
                  key={`${item.Date}`}
                  clientId={clientId}
                  title={item.Title}
                  description={item.Data.Description}
                  data={[
                    {
                      label:
                        item.Data.Status === 'Requested'
                          ? 'Requested Date'
                          : 'Completed Date',
                      value: format.formatDate(item.Date),
                      type: 'default',
                    },
                    {
                      label: 'Status',
                      value: item.Data.Status,
                      type: 'default',
                    },
                  ]}
                />
              );
            case 'PolicyCancellationRequests':
              return (
                <CommunicationPanelCard
                  key={`${item.Date}`}
                  clientId={clientId}
                  fileId={item.Data.FileId}
                  title={item.Title}
                  description={item.Description}
                  data={[
                    {
                      label:
                        item.Data.Status === 'Requested'
                          ? 'Requested Date'
                          : 'Completed Date',
                      value: format.formatDate(item.Date),
                      type: 'default',
                    },
                    {
                      label: 'Line of Business',
                      value: item.Data.LineOfBusiness,
                      type: 'default',
                    },
                    {
                      label: 'Carrier',
                      value: item.Data.Carrier,
                      type: 'default',
                    },
                    {
                      label: 'PolicyNumber',
                      value: item.Data.PolicyNumber,
                      type: 'default',
                    },
                    {
                      label: 'Status',
                      value: item.Data.Status,
                      type: 'default',
                    },
                  ]}
                />
              );
            case 'BindRequest':
              return (
                <CommunicationPanelCard
                  key={`${item.Date}`}
                  clientId={clientId}
                  title={item.Title}
                  description={item.Description}
                  data={[
                    {
                      label: 'Date Bind Request Submitted',
                      value: format.formatDate(item.Date),
                      type: 'default',
                    },
                    {
                      label: 'Line of Business',
                      value: item.Data.LineOfBusiness,
                      type: 'default',
                    },
                    {
                      label: 'Carrier',
                      value: item.Data.Carrier,
                      type: 'default',
                    },
                    {
                      label: 'Requested Effective Date',
                      value: item.Data.RequestedEffectiveDate,
                      type: 'default',
                    },
                  ]}
                />
              );
            case 'Endorsements':
              return (
                <CommunicationPanelCard
                  key={`${item.Data.EndorsementId}${item.Date}`}
                  clientId={clientId}
                  title={item.Title}
                  description={item.Description}
                  data={[
                    {
                      label: 'Endorsement #',
                      value: item.Data.EndorsementId,
                      type: 'default',
                    },
                    {
                      label: 'Request Date',
                      value: format.formatDate(item.Date),
                      type: 'default',
                    },
                    {
                      label: 'Policy #',
                      value: item.Data.PolicyId,
                      type: 'default',
                    },
                    {
                      label: 'Policy Number',
                      value: item.Data.PolicyNumber,
                      type: 'default',
                    },
                    {
                      label: 'Carrier',
                      value: item.Data.Carrier,
                      type: 'default',
                    },
                  ]}
                />
              );
            default:
              return null;
          }
        })}
      </section>
    </CommunicationsDispatch.Provider>
  );
});

CommunicationsPanel.propTypes = {
  clientId: PropTypes.string.isRequired,
};

export default CommunicationsPanel;
